import { Icon, type IconName } from '@/components/brand/Icon';
import { LinkButton } from '@/components/ui/Button';
import { MediaFrame } from '@/components/ui/MediaFrame';
import { Section } from '@/components/ui/primitives';
import { ACADEMY, useAcademy } from '@/lib/academy';
import { useI18n } from '@/lib/i18n';

export function ContactLocation(): React.JSX.Element {
  const { t } = useI18n();
  const academy = useAcademy();

  const openWeekdays = ACADEMY.openingHours[0];

  return (
    <Section>
      <div className="grid gap-16 lg:grid-cols-[1fr_520px]">
        <div className="flex flex-col gap-5">
          <p className="text-overline font-semibold uppercase text-accent">
            {t('contact.overline')}
          </p>
          <h2 className="font-display text-h2 font-normal text-primary">{t('contact.title')}</h2>
          <p className="text-body-lg text-secondary">{t('contact.sub')}</p>

          <ul className="flex flex-col gap-3.5 pt-3">
            <Fact icon="map-pin" href={academy.mapUrl} external>
              {academy.address}
            </Fact>
            <Fact icon="clock">{t('contact.hoursShort', { hours: openWeekdays.hours })}</Fact>
            <Fact icon="whatsapp" href={academy.whatsappUrl} external>
              {t('contact.whatsappLine', { phone: academy.phoneDisplay })}
            </Fact>
            <Fact icon="mail" href={`mailto:${academy.email}`}>
              {academy.email}
            </Fact>
          </ul>

          <div className="flex flex-wrap gap-3 pt-3">
            <LinkButton to="/booking/subject" size="lg">
              {t('nav.bookTrial')}
            </LinkButton>
            <a
              href={academy.whatsappUrl}
              target="_blank"
              rel="noreferrer noopener"
              className="inline-flex h-13 items-center gap-2 rounded-md border border-border-strong bg-surface px-8 text-body font-medium text-primary hover:bg-subtle"
            >
              <Icon name="whatsapp" size={20} />
              {t('contactPage.openWhatsapp')}
            </a>
          </div>
        </div>

        <MediaFrame ratio="subject" icon="map-pin" className="rounded-md" />
      </div>
    </Section>
  );
}

function Fact({
  icon,
  href,
  external,
  children,
}: {
  icon: IconName;
  href?: string | undefined;
  external?: boolean | undefined;
  children: React.ReactNode;
}): React.JSX.Element {
  const body = (
    <>
      <Icon name={icon} size={18} className="shrink-0 text-tertiary" />
      {children}
    </>
  );

  return (
    <li>
      {href ? (
        <a
          href={href}
          {...(external ? { target: '_blank', rel: 'noreferrer noopener' } : {})}
          className="flex items-center gap-2.5 text-body text-primary hover:underline"
        >
          {body}
        </a>
      ) : (
        <span className="flex items-center gap-2.5 text-body text-primary">{body}</span>
      )}
    </li>
  );
}
